// FlatList reports an unmeasured target through a synchronous callback and
// returns normally. Keep that failure attached to the caller: starting a timer
// in onScrollToIndexFailed creates a second, uncancellable scroll loop.
export function createChatIndexScroller(getList) {
  let currentAttempt = null;

  const onScrollToIndexFailed = (failure) => {
    if (currentAttempt) currentAttempt.failure = failure;
  };

  const scrollToIndex = (params) => {
    const list = getList();
    if (!list?.scrollToIndex) return { status: 'unavailable' };
    const attempt = { failure: null };
    currentAttempt = attempt;
    try {
      list.scrollToIndex(params);
      return attempt.failure
        ? { status: 'unmeasured', failure: attempt.failure }
        : { status: 'scrolled' };
    } catch (_) {
      // The target may have disappeared while the list was changing.
      return { status: 'stopped' };
    } finally {
      currentAttempt = null;
    }
  };

  // Only an explicit jump may estimate an unmeasured row's position. The
  // owning effect schedules retries and clears its timer on cancellation.
  // Preserve this budget across renders of the same request so streaming
  // content cannot repeatedly reset the attempt count or deadline.
  const createRequest = () => {
    let cancelled = false;
    let attempts = 0;
    let deadline = null;
    let lastEstimatedFrame = -Infinity;
    return {
      cancel: () => { cancelled = true; },
      attempt: (params) => {
        if (cancelled || attempts >= 20 || (deadline !== null && Date.now() >= deadline)) {
          return { status: 'stopped' };
        }
        if (deadline === null) deadline = Date.now() + 3000;
        attempts += 1;
        const result = scrollToIndex(params);
        if (result.status === 'unmeasured') {
          const failure = result.failure;
          const highestMeasured = failure.highestMeasuredFrameIndex ?? failure.highestMeasuredCellIndex ?? -1;
          // Repeating the same estimated jump while measurements are stuck
          // just fights the reader and the list's layout. Wait for progress.
          if (highestMeasured > lastEstimatedFrame) {
            lastEstimatedFrame = highestMeasured;
            const average = failure.averageItemLength > 0 ? failure.averageItemLength : 220;
            const offset = Math.max(0, average * params.index);
            if (Number.isFinite(offset)) {
              try {
                getList()?.scrollToOffset?.({ offset, animated: false });
              } catch (_) { /* the list may have unmounted */ }
            }
          }
        }
        return result;
      },
    };
  };

  return { scrollToIndex, onScrollToIndexFailed, createRequest };
}
